import {isToday, isAfter} from 'date-fns';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  closeDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  TimeType,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {
  BusinessCaseActivityResponseBody,
  InterestActivityResponseBody,
  SalesActivityManualTypeEnumSchema,
  useCreateBusinessCaseActivityMutation,
  useCreateInterestActivityMutation,
  useDeleteSalesActivityMutation,
  usePatchSalesActivityMutation,
} from '@dms/api/sales';
import {
  DirectSaleActivityResponseBody,
  DirectSaleManualActivityKindSchema,
  useCreateDirectSaleActivityMutation,
  usePatchDirectSaleActivityMutation,
  useDeleteDirectSaleActivityMutation,
} from '@dms/api/warehouse';
import {
  useCreateServiceCaseActivityMutation,
  useCreateWorkshopInterestActivityMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {PredefinedNotes, handleApiError, usePermissions} from '@dms/shared';

import {TestIdProps, parseDate, suffixTestId} from 'shared/web';

import {LogContactDialogBox} from './components/LogContactDialogBox';
import {LogContactDialogSchemaType, LogContactDialogSchema} from './schemas/LogContactDialogSchema';

type BusinessCaseCreateProps = {
  directSaleId?: never;
  directSaleActivity?: never;
  businessCaseId: string;
  interestId?: never;
  serviceCaseId?: never;
  workshopInterestId?: never;
  salesActivity?: never;
};

type InterestCreateProps = {
  directSaleId?: never;
  directSaleActivity?: never;
  interestId: string;
  businessCaseId?: never;
  serviceCaseId?: never;
  workshopInterestId?: never;
  salesActivity?: never;
};

type ServiceCaseCreateProps = {
  directSaleId?: never;
  directSaleActivity?: never;
  serviceCaseId: string;
  businessCaseId?: never;
  interestId?: never;
  workshopInterestId?: never;
  salesActivity?: never;
};

type WorkshopInterestCreateProps = {
  directSaleId?: never;
  directSaleActivity?: never;
  workshopInterestId: string;
  businessCaseId?: never;
  interestId?: never;
  serviceCaseId?: never;
  salesActivity?: never;
};

type SalesActivityEditProps = {
  directSaleId?: never;
  directSaleActivity?: never;
  salesActivity: BusinessCaseActivityResponseBody | InterestActivityResponseBody;
  businessCaseId?: never;
  interestId?: never;
  serviceCaseId?: never;
  workshopInterestId?: never;
};

type DirectSaleProps = {
  directSaleId: string;
  directSaleActivity?: DirectSaleActivityResponseBody;
  businessCaseId?: never;
  interestId?: never;
  serviceCaseId?: never;
  workshopInterestId?: never;
  salesActivity?: never;
};

type LogContactDialogProps = TestIdProps &
  (
    | DirectSaleProps
    | BusinessCaseCreateProps
    | InterestCreateProps
    | ServiceCaseCreateProps
    | WorkshopInterestCreateProps
    | SalesActivityEditProps
  );

export function LogContactDialog(props: LogContactDialogProps) {
  const [createInterestSalesActivity, {isLoading: isCreateInterestActivityLoading}] =
    useCreateInterestActivityMutation();
  const [createBusinessCaseActivity, {isLoading: isCreateBusinessCaseActivityLoading}] =
    useCreateBusinessCaseActivityMutation();
  const [createWorkshopInterestActivity, {isLoading: isCreateWorkshopInterestActivityLoading}] =
    useCreateWorkshopInterestActivityMutation();
  const [createServiceCaseActivity, {isLoading: isCreateServiceCaseActivityLoading}] =
    useCreateServiceCaseActivityMutation();
  const [updateSalesActivity, {isLoading: isUpdateSalesActivityLoading}] =
    usePatchSalesActivityMutation();
  const [deleteSalesActivity, {isLoading: isDeleteSalesActivityLoading}] =
    useDeleteSalesActivityMutation();
  const [createDirectSaleActivity, {isLoading: isCreatingDirectSaleActivity}] =
    useCreateDirectSaleActivityMutation();
  const [patchDirectSaleActivity, {isLoading: isUpdatingDirectSaleActivity}] =
    usePatchDirectSaleActivityMutation();
  const [deleteDirectSaleActivity, {isLoading: isDeletingDirectSaleActivity}] =
    useDeleteDirectSaleActivityMutation();
  const [canCreate, canUpdate, canDelete] = usePermissions({
    permissionKeys: [
      'directSaleActivityCreate',
      'directSaleActivityUpdate',
      'directSaleActivityDelete',
    ],
  });
  const isDirectSale = props.directSaleId !== undefined;
  const activity = props.directSaleActivity ?? props.salesActivity;
  const isManualDirectSaleActivity =
    props.directSaleActivity?.isEditable === true &&
    DirectSaleManualActivityKindSchema.safeParse(props.directSaleActivity.activityType).success;
  const canSave =
    !isDirectSale ||
    (props.directSaleActivity ? isManualDirectSaleActivity && canUpdate : canCreate);
  const canRemove = !isDirectSale || (isManualDirectSaleActivity && canDelete);
  const formatDateTime = useDateTimeFormatter();

  const handleClose = () => {
    closeDialog('LOG_CONTACT_DIALOG');
  };

  const salesActivityId = props.salesActivity?.id;

  const handleDelete = async () => {
    if (isDirectSale) {
      if (canRemove && props.directSaleActivity) {
        await deleteDirectSaleActivity({
          directSaleId: props.directSaleId,
          activityId: props.directSaleActivity.id,
        })
          .unwrap()
          .then(handleClose)
          .catch(handleApiError);
      }
      return;
    }
    if (salesActivityId) {
      await deleteSalesActivity({salesActivityId}).unwrap().then(handleClose);
    }
  };

  const handleSubmit: FormSubmitHandler<LogContactDialogSchemaType> = async (data) => {
    if (!canSave) {
      return;
    }
    const dateTime = parseDate(data.date);
    const [hours, minutes] = data.time?.split(':').map(Number) ?? [0, 0];
    dateTime.setHours(hours, minutes); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

    const activityBody = {
      activityType: data.type[0],
      createdAt: dateTime.toISOString(),
      description: data.note,
      subject: data.subject,
    };

    const request: Promise<unknown> = match(props)
      .with({directSaleId: Pattern.string}, ({directSaleId, directSaleActivity}) =>
        directSaleActivity
          ? patchDirectSaleActivity({
              directSaleId,
              activityId: directSaleActivity.id,
              body: activityBody,
            }).unwrap()
          : createDirectSaleActivity({directSaleId, body: activityBody}).unwrap()
      )
      .with({businessCaseId: Pattern.string}, ({businessCaseId}) =>
        createBusinessCaseActivity({
          businessCaseId,
          createSalesActivityRequestBody: activityBody,
        }).unwrap()
      )
      .with({interestId: Pattern.string}, ({interestId}) =>
        createInterestSalesActivity({
          interestId,
          createSalesActivityRequestBody: activityBody,
        }).unwrap()
      )
      .with({workshopInterestId: Pattern.string}, ({workshopInterestId}) =>
        createWorkshopInterestActivity({
          interestId: workshopInterestId,
          createWorkshopInterestActivityRequestBody: activityBody,
        }).unwrap()
      )
      .with({serviceCaseId: Pattern.string}, ({serviceCaseId}) =>
        createServiceCaseActivity({
          serviceCaseId,
          createServiceCaseActivityRequestBody: activityBody,
        }).unwrap()
      )
      .with({salesActivity: {id: Pattern.string}}, ({salesActivity}) =>
        updateSalesActivity({
          salesActivityId: salesActivity.id,
          patchSalesActivityRequestBody: activityBody,
        }).unwrap()
      )
      .exhaustive();

    await request.then(handleClose).catch(handleApiError);
  };

  const isLoading =
    isCreatingDirectSaleActivity ||
    isUpdatingDirectSaleActivity ||
    isDeletingDirectSaleActivity ||
    isCreateBusinessCaseActivityLoading ||
    isCreateInterestActivityLoading ||
    isCreateWorkshopInterestActivityLoading ||
    isCreateServiceCaseActivityLoading ||
    isUpdateSalesActivityLoading ||
    isDeleteSalesActivityLoading;

  const currentDate = parseDate(new Date());
  const currentTime =
    `${currentDate?.getHours() ?? '00'}:${currentDate?.getMinutes() ?? '00'}` as TimeType;

  const manualType = SalesActivityManualTypeEnumSchema.safeParse(activity?.activityType);
  const defaultValues = {
    subject: activity?.subject ?? '',
    type: manualType.success ? [manualType.data] : [],
    date: activity?.timestamp ? parseDate(activity?.timestamp) : new Date(),
    time: activity?.timestamp
      ? formatDateTime('timeShort', parseDate(activity?.timestamp))
      : currentTime,
    note: activity?.description ?? '',
  };

  return (
    <>
      <LogContactDialogBox />
      <Form<LogContactDialogSchemaType>
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        experimentalZodSchema={LogContactDialogSchema}
      >
        {(control, formApi) => {
          const selectedDate = formApi.watch('date');
          const selectedTime = formApi.watch('time');

          // Check if date is today and time is in the future
          if (isToday(selectedDate as Date) && selectedTime) {
            // Create date objects for comparison
            const now = new Date();
            const today = new Date();

            // Parse the selected time and set it to today's date for comparison
            const [hours, minutes] = selectedTime.split(':').map(Number);
            today.setHours(hours, minutes, 0, 0); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

            // If selected time is in the future, reset to current time
            if (isAfter(today, now)) {
              setTimeout(() => formApi.setValue('time', currentTime), 0);
            }
          }

          return (
            <Box paddingBottom={0}>
              <VStack spacing={4}>
                <VStack spacing={1}>
                  <HStack justify="space-between" align="flex-end">
                    <Label>{`* ${i18n.t('entity.activityPanel.logContact.subject')}`}</Label>
                    <PredefinedNotes
                      note={formApi.watch('subject') ?? null}
                      onPrefill={(note) => formApi.setValue('subject', note)}
                      resource="LOG_CONTACT"
                      context="log_contact_subject"
                      isLinkVariant
                      data-testid={suffixTestId('predefinedNotes', props)}
                    />
                  </HStack>
                  <FormField control={control} isRequired name="subject" type="text" />
                </VStack>

                <FormField
                  isRequired
                  isMultiple={false}
                  control={control}
                  label={i18n.t('entity.activityPanel.logContact.type')}
                  name="type"
                  type="chips"
                  options={
                    isDirectSale
                      ? [
                          ...typeOptions,
                          {
                            label: i18n.t('entity.activityPanel.activity.messageFromCustomer'),
                            value: 'MESSAGE_FROM_CUSTOMER',
                          },
                        ]
                      : typeOptions
                  }
                />

                <HStack spacing={4}>
                  <FormField
                    isRequired
                    control={control}
                    name="date"
                    type="date"
                    label={i18n.t('entity.activityPanel.logContact.date')}
                    maxDate={currentDate}
                  />
                  <FormField
                    control={control}
                    name="time"
                    type="time"
                    label={i18n.t('entity.activityPanel.logContact.time')}
                    maxTime={
                      isToday(formApi.watch('date') as Date) || formApi.watch('date') === null
                        ? currentTime
                        : undefined
                    }
                  />
                </HStack>

                <VStack spacing={1}>
                  <HStack justify="space-between" align="flex-end">
                    <Label>{`${i18n.t('general.labels.note')}`}</Label>
                    <PredefinedNotes
                      note={formApi.watch('note') ?? null}
                      onPrefill={(note) => formApi.setValue('note', note)}
                      resource="LOG_CONTACT"
                      context="log_contact_note"
                      isLinkVariant
                      data-testid={suffixTestId('predefinedNotes', props)}
                    />
                  </HStack>
                  <FormField control={control} name="note" type="text" />
                </VStack>

                <HStack justify={activity && canRemove ? 'space-between' : 'flex-end'} width="100%">
                  <Show when={activity && canRemove}>
                    <Button
                      title={i18n.t('general.actions.delete')}
                      variant="dangerOutlined"
                      isDisabled={isLoading}
                      onClick={handleDelete}
                    />
                  </Show>
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      onClick={closeCurrentDialog}
                      isDisabled={isLoading}
                      title={i18n.t('general.actions.cancel')}
                    />
                    <Show when={canSave}>
                      <FormButton
                        variant="primary"
                        type="submit"
                        control={control}
                        isLoading={isLoading}
                        title={i18n.t('general.actions.save')}
                      />
                    </Show>
                  </ButtonGroup>
                </HStack>
              </VStack>
            </Box>
          );
        }}
      </Form>
    </>
  );
}

const typeOptions = [
  {
    label: i18n.t('entity.activityPanel.logContact.callToCustomer'),
    value: 'CALL_TO_CUSTOMER',
  },
  {
    label: i18n.t('entity.activityPanel.logContact.callFromCustomer'),
    value: 'CALL_FROM_CUSTOMER',
  },
  {
    label: i18n.t('entity.activityPanel.logContact.customerVisit'),
    value: 'CUSTOMER_VISIT',
  },
  {
    label: i18n.t('entity.activityPanel.logContact.messageToCustomer'),
    value: 'MESSAGE_TO_CUSTOMER',
  },
];
